import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";
import { BulkUploadProductRow } from "@/components/menu/BulkUploadProductRow";
import { BulkUploadToolbar } from "@/components/menu/BulkUploadToolbar";

export function BulkImageUpload({ open, onOpenChange, products, categories, onRefresh }) {
  const { authAxios } = useAuth();
  const [uploads, setUploads] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState({ current: 0, total: 0 });
  const [genMode, setGenMode] = useState("");
  const getCatName = (catId) => categories.find(c => c.id === catId)?.name || "";

  const handleFileSelect = async (productId, file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Solo imagenes permitidas");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagen muy grande (max 5MB)");
      return;
    }

    setUploads(prev => ({ ...prev, [productId]: { status: "uploading", preview: URL.createObjectURL(file) } }));

    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authAxios.post("/menu/upload-media", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploads(prev => ({ ...prev, [productId]: { status: "uploaded", preview: prev[productId].preview, url: res.data.url } }));
    } catch {
      setUploads(prev => ({ ...prev, [productId]: { status: "error", preview: prev[productId]?.preview } }));
      toast.error("Error al subir imagen");
    }
  };

  const handleMultiFileSelect = async (files) => {
    const productsWithout = products.filter(p => !p.image_url && !uploads[p.id]);
    const fileList = Array.from(files).filter(f => f.type.startsWith("image/"));

    const matched = [];
    const unmatched = [];

    for (const file of fileList) {
      const fname = file.name.replace(/\.[^.]+$/, "").toLowerCase().replace(/[_-]/g, " ").trim();
      const match = productsWithout.find(p =>
        !matched.some(m => m.productId === p.id) &&
        (p.name.toLowerCase().includes(fname) || fname.includes(p.name.toLowerCase()) ||
         p.name.toLowerCase().split(" ").some(w => w.length > 3 && fname.includes(w)))
      );
      if (match) {
        matched.push({ productId: match.id, file });
      } else {
        unmatched.push(file);
      }
    }

    for (const { productId, file } of matched) {
      await handleFileSelect(productId, file);
    }

    const remaining = productsWithout.filter(p => !matched.some(m => m.productId === p.id) && !uploads[p.id]);
    for (let i = 0; i < unmatched.length && i < remaining.length; i++) {
      await handleFileSelect(remaining[i].id, unmatched[i]);
    }

    const totalAssigned = matched.length + Math.min(unmatched.length, remaining.length);
    if (matched.length > 0) toast.success(`${matched.length} foto(s) associada(s) por nome`);
    if (fileList.length > totalAssigned) {
      toast.info(`${fileList.length - totalAssigned} imagen(es) sin producto disponible`);
    }
  };

  const generateWithAI = async () => {
    const productsWithout = products.filter(p => !p.image_url && !uploads[p.id]);
    if (productsWithout.length === 0) {
      toast.info("Todos los productos ya tienen imagen");
      return;
    }

    setGenerating(true);
    setGenProgress({ current: 0, total: productsWithout.length });
    let success = 0;
    let failed = 0;

    for (const product of productsWithout) {
      setGenProgress(prev => ({ ...prev, current: prev.current + 1 }));
      try {
        const res = await authAxios.post("/menu/generate-image", {
          product_name: product.name,
          category_name: getCatName(product.category_id),
        }, { timeout: 120000 });

        if (res.data?.url) {
          setUploads(prev => ({
            ...prev,
            [product.id]: { status: "uploaded", preview: `${API_URL}${res.data.url}`, url: res.data.url }
          }));
          success++;
        }
      } catch (err) {
        failed++;
        setUploads(prev => ({ ...prev, [product.id]: { status: "error" } }));
      }
    }

    setGenerating(false);
    if (success > 0) toast.success(`${success} foto(s) gerada(s) com IA`);
    if (failed > 0) toast.error(`${failed} foto(s) falharam`);
  };

  const generateDescriptions = async () => {
    const noDesc = products.filter(p => !p.description || p.description.trim() === "");
    if (noDesc.length === 0) {
      toast.info("Todos los productos ya tienen descripcion");
      return;
    }

    setGenerating(true);
    setGenMode("desc");
    setGenProgress({ current: 0, total: noDesc.length });
    let success = 0;

    for (const product of noDesc) {
      setGenProgress(prev => ({ ...prev, current: prev.current + 1 }));
      try {
        const res = await authAxios.post("/menu/generate-description", {
          product_name: product.name,
          category_name: getCatName(product.category_id),
        }, { timeout: 30000 });

        if (res.data?.description) {
          await authAxios.put(`/menu/products/${product.id}`, {
            ...product,
            description: res.data.description,
            category_id: product.category_id,
          });
          success++;
        }
      } catch { /* skip */ }
    }

    setGenerating(false);
    setGenMode("");
    if (success > 0) {
      toast.success(`${success} descripcion(es) gerada(s)`);
      onRefresh();
    }
  };

  const removeUpload = (productId) => {
    setUploads(prev => {
      const next = { ...prev };
      if (next[productId]?.preview) URL.revokeObjectURL(next[productId].preview);
      delete next[productId];
      return next;
    });
  };

  const pendingCount = Object.values(uploads).filter(u => u.status === "uploaded").length;

  const saveAll = async () => {
    setSaving(true);
    let success = 0;
    let failed = 0;

    for (const [productId, upload] of Object.entries(uploads)) {
      if (upload.status !== "uploaded" || !upload.url) continue;
      const product = products.find(p => p.id === productId);
      if (!product) continue;

      try {
        await authAxios.put(`/menu/products/${productId}`, {
          ...product,
          image_url: upload.url,
          category_id: product.category_id,
        });
        success++;
        setUploads(prev => ({ ...prev, [productId]: { ...prev[productId], status: "saved" } }));
      } catch {
        failed++;
        setUploads(prev => ({ ...prev, [productId]: { ...prev[productId], status: "error" } }));
      }
    }

    if (success > 0) toast.success(`${success} producto(s) actualizado(s)`);
    if (failed > 0) toast.error(`${failed} producto(s) fallaron`);

    setSaving(false);
    if (success > 0) onRefresh();
  };

  const handleClose = (val) => {
    if (!val) {
      Object.values(uploads).forEach(u => { if (u.preview) URL.revokeObjectURL(u.preview); });
      setUploads({});
    }
    onOpenChange(val);
  };

  const productsWithoutImage = products.filter(p => !p.image_url && !uploads[p.id]).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-bold uppercase">
            <Upload className="w-5 h-5 text-emerald-600" /> Subir Fotos en Masa
          </DialogTitle>
        </DialogHeader>

        <BulkUploadToolbar
          pendingCount={pendingCount}
          productsWithoutImage={productsWithoutImage}
          generating={generating}
          genMode={genMode}
          genProgress={genProgress}
          saving={saving}
          onGenerateWithAI={generateWithAI}
          onGenerateDescriptions={generateDescriptions}
          onMultiFileSelect={handleMultiFileSelect}
          onSaveAll={saveAll}
        />

        {generating && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 mb-2">
            <div className="flex items-center gap-2 text-sm text-purple-700">
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">IA gerando fotos... {genProgress.current}/{genProgress.total}</span>
            </div>
            <div className="w-full bg-purple-200 rounded-full h-2 mt-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${genProgress.total > 0 ? (genProgress.current / genProgress.total) * 100 : 0}%` }}
              />
            </div>
            <p className="text-xs text-purple-500 mt-1">Cada imagem leva ~30s. Nao feche esta janela.</p>
          </div>
        )}

        <div className="overflow-y-auto flex-1 -mx-1 px-1 space-y-1.5" data-testid="bulk-product-list">
          {products.map(product => (
            <BulkUploadProductRow
              key={product.id}
              product={product}
              upload={uploads[product.id]}
              catName={getCatName(product.category_id)}
              onFileSelect={handleFileSelect}
              onRemove={removeUpload}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
